import { Component, EventEmitter, Inject, Injector, Input, Output, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime } from 'rxjs';
import { AbstractComponent } from '../../../../commons/abstract.component';
import { UtilHelper } from '../../../../helpers/util.helper';
import { ContactGroup } from '../../../../models/contact-group.model';
import { Contact } from '../../../../models/contact.model';
import { CountryModel } from '../../../../models/country.model';
import { CustomField } from '../../../../models/custom-field.model';
import { Department } from '../../../../models/department.model';
import { ContactGenderEnum } from '../../../../models/enum/contact-gender.type.enum';
import { Tag } from '../../../../models/tag.model';
import { User } from '../../../../models/user.model';
import { ContactGroupService } from '../../../../services/contact-group.service';
import { ContactService } from '../../../../services/contact.service';
import { CustomFieldService } from '../../../../services/custom-field.service';
import { DepartmentService } from '../../../../services/department.service';
import { TagService } from '../../../../services/tag.service';
import { TeamService } from '../../../../services/team.service';
import { Team } from '../../../../models/team.model';
import { UF_OPTIONS } from '../../../../models/enum/uf.enum';

@Component({
    selector: 'contact-edit-component',
    templateUrl: './contact-edit.v2.component.html',
    // templateUrl: './contact-edit.component.html',
    styleUrls: ['./contact-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactEditComponent extends AbstractComponent {
  filterControlGroup: FormControl = new FormControl('');
  filterControlTags: FormControl = new FormControl('');
  filterCustomField: FormControl = new FormControl('');
  filterControlDeparment: FormControl = new FormControl('');
  filterControlAttendant: FormControl = new FormControl('');

  @Input() user: User;
  @Output() onDelete: EventEmitter<User> = new EventEmitter<User>();
  @Output() onEdit: EventEmitter<User> = new EventEmitter<User>();

  contact: Contact;
  currentUser: any;
  listContactGroup: Array<ContactGroup> = new Array<ContactGroup>();
  allGroups: Array<ContactGroup> = new Array<ContactGroup>();

  departmentsList: Array<Department> = new Array<Department>();
  allDepartments: Array<Department> = new Array<Department>();

  attendantList: Array<User> = new Array<User>();
  allAttendant: Array<User> = new Array<User>();

  countries: CountryModel[] = [];
  selectedCountryCode!: string;
  phoneMask!: string;

  listTags: Array<Tag> = new Array<Tag>();
  allTags: Array<Tag> = new Array<Tag>();

  listGenders: Array<ContactGenderEnum> =
    new Array<ContactGenderEnum>(ContactGenderEnum.MALE, ContactGenderEnum.FEMALE, ContactGenderEnum.OTHER);

  listFieldsCustom: Array<CustomField> = new Array<CustomField>();
  allFieldsCustom: Array<CustomField> = new Array<CustomField>();
  step: number = 0;

  ufOptions = UF_OPTIONS;

  startAt = new Date();

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ContactEditComponent>,
    public contactService: ContactService,
    public contactGrupoService: ContactGroupService,
    public departmentService: DepartmentService,
    public tagService: TagService,
    public teamService: TeamService,
    private fieldsCustomService: CustomFieldService,
    @Inject(MAT_DIALOG_DATA) public data: Contact,
  ) {
    super(injector);
    this.contact = {} as Contact;
    this.createForm();

    this.getListGroups();
    this.fetchFilterGroups();

    this.loadDepartments();
    this.fetchFilterDepartments();

    this.getListTags();
    this.fetchFilterTags();

    this.fetchFilterAttendant();
  }

  ngOnInit() {
    this.currentUser = this.getCurrentUserUser();
    this.countries = UtilHelper.getCountries();

    this.isNew = (!!this.data ? false : true);

    if (!this.isNew) {
      this.contact = { ...this.data };

      this.formGroup.patchValue(this.contact);

      this.formGroup.get('groups')?.setValue(this.contact?.groups?.map((group: ContactGroup) => group.name ? group._id : group));

      this.formGroup.get('tags')?.setValue(this.contact?.tags?.map((tag) => tag.title ? tag._id : tag));

      let phoneNumber = this.formGroup.get('phone')?.value;
      let country = UtilHelper.getCountryByPhoneNumber(phoneNumber);

      if (country) {
        this.selectedCountryCode = country.code;

        let phoneNumberWithoutCountryCode = phoneNumber.replace(country.code.replace('+', ''), '');

        this.formGroup.patchValue({
          ...this.contact,
          phone: phoneNumberWithoutCountryCode,
          country: country.code,
        });
      }

      this.formGroup.patchValue({
        ...this.contact,
        selectedFields: this.data?.customFields?.map(field => field._id),
      });
      this.populateCustomFields(this.data?.customFields);
    } else {
      this.selectedCountryCode = this.countries[0].code;
    }

    const selectedCountry = this.countries.find(country => country.code === this.selectedCountryCode);
    this.phoneMask = selectedCountry?.mask;
  }

  get today(): Date {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }

  notInFuture = (d: Date | null): boolean => {
    return (d ?? new Date()) <= this.today;
  }

  onCountryChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedCountryCode = selectElement.value;

    const selectedCountry = this.countries.find(country => country.code === this.selectedCountryCode);
    this.phoneMask = selectedCountry?.mask;
  }

  protected createForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [null, Validators.required],
      image: [null],
      phone: [null, (control: any) => (!!control.value || !!this.contact?.whatsapp?.bsuid) ? null : { required: true }],
      email: [null, [Validators.pattern(this.emailRegex)]],
      zip: [null],
      address: [null],
      city: [null],
      state: [null],
      groups: [null],
      country: [null],
      tags: [null],
      tin: [null],
      contactCompany: [null],
      contactPosition: [null],
      gender: [ContactGenderEnum.OTHER],
      birthDate: [null],
      customFields: new FormArray([]),
      selectedFields: [null],
      attendant: [null],
      department: [null],
    });

    this.formGroup.valueChanges.subscribe(value => {
      Object.assign(this.contact, value);
    });

    this.getListFieldsCustom();
    this.fetchFilterCustomFields();
  }

  close(): void {
    this.dialogRef.close();
  }

  nextStepValidationForm() {
    this.step = 1
  }

  newConversation() {
    this.router.navigate(['/attendance/panel'], {
      queryParams: { contactId: this.contact._id }
    });
    this.dialogRef.close();
  }

  save() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    if (this.formGroup.get('attendant')?.value && this.formGroup.get('department')?.value) {
      this.contact.customerPortfolio = {
        user: this.formGroup.get('attendant')?.value,
        department: this.formGroup.get('department')?.value
      }
    } else {
      this.contact.customerPortfolio = null;
    }

    this.loading = true;
    this.setGroups();
    this.setPhone();
    this.setTags();

    if (this.isNew) {
      this.contactService.create(this.contact).subscribe({
        next: (value) => {
          this.loading = false
          this.alertService.success(this.t('shared.contactEditLib.contactSavedSuccess'));
          this.nextStepValidationForm()
          this.contact = value
        },
        error: () => this.loading = false,
      });
    } else {
      this.contactService.update(this.contact._id, this.contact).subscribe({
        next: (value) => {
          this.loading = false
          this.dialogRef.close(value);
          this.alertService.success(this.t('shared.contactEditLib.contactUpdatedSuccess'));
        },
        error: () => this.loading = false,
      });
    }
  }

  private setTags(): void {
    const control = this.formGroup.get('tags');

    // The control is seeded once, from the dialog data. If the user never touched it,
    // omit `tags` from the payload entirely — the update endpoint only $sets the keys
    // it receives, so leaving it out preserves whatever tags the contact has now.
    // Sending it back would either blank the list (control never populated) or revert
    // tags applied since the dialog opened.
    if (!control?.dirty) {
      delete this.contact.tags;
      return;
    }

    this.contact.tags = ((control.value ?? []) as Array<string>).map((idTag: string) => <Tag>{ _id: idTag });
  }

  private setPhone(): void {
    const phone = this.formGroup.get('phone')?.value;
    if (!phone) {
      this.contact.phone = null;
      return;
    }
    const fullPhoneNumber = `${this.selectedCountryCode}${phone}`.replace('+', '');
    this.contact.phone = fullPhoneNumber;
  }

  private setGroups(): void {
    const groups = this.formGroup.get('groups').value;
    this.contact.groups = [];
    groups?.forEach((idGroup: string) => {
      this.contact.groups.push({ _id: idGroup } as ContactGroup);
    });
  }

  private loadDepartments() {
    this.departmentService.getList().subscribe({
      next: (departments: Array<Department>) => {
        this.departmentsList = departments;
        this.allDepartments = departments;
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  changeDepartment() {
    this.formGroup.patchValue({ user: null });
    this.loadAttendant();
  }

  loadAttendant() {
    const idDepartment = this.formGroup.get('department')?.value;
    if (!idDepartment) {
      this.attendantList = [];
      this.allAttendant = [];
      return;
    }
    this.teamService.getByDepartment(idDepartment).subscribe({
      next: (teams: Team[]) => {
        if (teams.length > 0) {
          this.attendantList = teams[0].agents;
          this.allAttendant = teams[0].agents;
        } else {
          this.attendantList = [];
          this.allAttendant = [];
        }
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  keyPressOnlyNumbers(event: any) {
    const pattern = /[0-9\+\-\ ]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode != 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  onCep(cep: string): void {
    if (cep.length === 8) {
      this.getCep(cep);
    }
  }

  private getCep(cep: string) {
    return this.contactService.searchCep(cep).subscribe({
      next: (res) => {
        this.formGroup.patchValue({
          address: res.street,
          city: res.city,
          state: res.state,
        });
      },
      error: (err) => this.alertService.error(err),
    });
  }

  private getListTags(): void {
    this.tagService.getList().subscribe({
      next: (value) => {
        this.allTags = value;
        this.listTags = value;
      },
      error: () => this.alertService.error(),
    });
  }

  private getListFieldsCustom(): void {
    this.fieldsCustomService.getList().subscribe({
      next: (list: Array<CustomField>) => {
        this.allFieldsCustom = list;
        this.listFieldsCustom = list;
      },
      error: () => this.alertService.error(),
    });
  }

  updateCustomFields(selectedFieldIds: Array<string>): void {
    const customFieldsArray = this.formGroup.get('customFields') as FormArray;

    for (let i = customFieldsArray.length - 1; i >= 0; i--) {
      const fieldControl = customFieldsArray.at(i);
      const fieldId = fieldControl.get('_id')?.value;

      if (!selectedFieldIds.includes(fieldId)) {
        customFieldsArray.removeAt(i);
      }
    }

    selectedFieldIds.forEach((fieldId) => {
      const existingField = customFieldsArray.controls.find(control => control.get('_id')?.value === fieldId);

      if (!existingField) {
        const field = this.listFieldsCustom.find((item) => item._id === fieldId);
        if (field) {
          customFieldsArray.push(this.createCustomFieldGroup(field));
        }
      }
    });
  }

  private populateCustomFields(customFields: Array<{ _id: string, name: string, description: string, value: unknown }>): void {
    if (!!customFields) {
      const customFieldsArray = this.formGroup.get('customFields') as FormArray;
      customFieldsArray.clear();

      customFields.forEach(field => {
        customFieldsArray.push(this.formBuilder.group({
          _id: [field._id],
          name: [field.name],
          description: [field.description],
          value: [field.value],
        }));
      });
    }
  }

  private createCustomFieldGroup(field: CustomField): FormGroup {
    return new FormGroup({
      _id: new FormControl(field._id),
      name: new FormControl(field.name),
      description: new FormControl(field.description),
      value: new FormControl('', Validators.required),
    });
  }

  get customFields() {
    return this.formGroup.controls['customFields'] as FormArray;
  }

  getFieldName(fieldId: string): string {
    const field = this.listFieldsCustom.find(item => item._id === fieldId);
    return field ? field.name : '';
  }

  getFieldType(fieldId: string): string {
    const field = this.listFieldsCustom.find(item => item._id === fieldId);
    return field ? field.type : '';
  }

  private getListGroups(): void {
    this.contactGrupoService.getList().subscribe({
      next: value => {
        this.listContactGroup = value;
        this.allGroups = value;
      },
    });
  }

  private fetchFilterGroups(): void {
    this.filterControlGroup.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('groups')?.value || [];

      this.allGroups = this.listContactGroup.filter(group =>
        group.name.toLowerCase().includes(value.toLowerCase()) &&
        !currentValue.includes(group._id)
      );

      currentValue.forEach(groupId => {
        const existingGroup = this.listContactGroup.find(group => group._id === groupId);
        if (existingGroup) {
          this.allGroups.push(existingGroup);
        }
      });
    });
  }

  onFocusGroup(): void {
    this.allGroups = this.listContactGroup;
  }

  private fetchFilterTags(): void {
    this.filterControlTags.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('tags')?.value || [];

      this.allTags = this.listTags.filter(tag =>
        tag.title.toLowerCase().includes(value.toLowerCase()) &&
        !currentValue.includes(tag._id)
      );

      currentValue.forEach(tagId => {
        const existingTags = this.listTags.find(tag => tag._id === tagId);
        if (existingTags) {
          this.allTags.push(existingTags);
        }
      });
    });
  }

  onFocusTags(): void {
    this.allTags = this.listTags;
  }

  private fetchFilterDepartments(): void {
    this.filterControlDeparment.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('department')?.value || null;

      this.allDepartments = this.departmentsList.filter(department =>
        department.name.toLowerCase().includes(value.toLowerCase()) &&
        department._id !== currentValue
      );

      const existingDepartment = this.departmentsList.find(department => department._id === currentValue);
      if (existingDepartment) {
        this.allDepartments.push(existingDepartment);
      }
    });
  }

  onFocusDepartments(): void {
    this.allDepartments = this.departmentsList;
  }

  private fetchFilterAttendant(): void {
    this.filterControlAttendant.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('attendant')?.value || null;

      this.allAttendant = this.attendantList.filter(attendant =>
        attendant.name.toLowerCase().includes(value.toLowerCase()) &&
        attendant._id !== currentValue
      );

      const existingAttendant = this.attendantList.find(attendant => attendant._id === currentValue);
      if (existingAttendant) {
        this.allAttendant.push(existingAttendant);
      }
    });
  }

  onFocusAttendant(): void {
    this.allAttendant = this.attendantList;
  }

  private fetchFilterCustomFields(): void {
    this.filterCustomField.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('selectedFields')?.value || [];

      this.allFieldsCustom = this.listFieldsCustom.filter(field =>
        field.name.toLowerCase().includes(value.toLowerCase()) &&
        !currentValue.includes(field._id)
      );

      currentValue.forEach(fieldId => {
        const existingFields = this.listFieldsCustom.find(field => field._id === fieldId);
        if (existingFields) {
          this.allFieldsCustom.push(existingFields);
        }
      });
    });
  }

  onFocusCustomFields(): void {
    this.allFieldsCustom = this.listFieldsCustom;
  }

}
